import {
	type CellData,
	columnFacetingFeature,
	columnFilteringFeature,
	columnOrderingFeature,
	columnResizingFeature,
	columnSizingFeature,
	columnVisibilityFeature,
	filterFn_arrIncludes,
	filterFn_equals,
	filterFn_includesString,
	filterFn_inDateRange,
	filterFn_inNumberRange,
	filterFn_weakEquals,
	globalFilteringFeature,
	metaHelper,
	type ReactTable,
	type RowData,
	rowPaginationFeature,
	rowSelectionFeature,
	rowSortingFeature,
	sortFn_alphanumeric,
	sortFn_basic,
	sortFn_datetime,
	sortFn_text,
	type Table,
	type TableFeature,
	type TableFeatures,
	tableFeatures,
} from '@tanstack/react-table';

import type { ColumnFilterDef } from './types';

declare module '@tanstack/table-core' {
	interface Plugins {
		columnFilterDefFeature: TableFeature;
	}

	interface ColumnDef_FeatureMap<
		TFeatures extends TableFeatures,
		TData extends RowData,
		TValue extends CellData,
	> {
		columnFilterDefFeature: {
			filter?: ColumnFilterDef<TData, TValue>;
		};
	}
}

const columnFilterDefFeature: TableFeature = {};

export interface DataTableColumnMeta {
	/**
	 * This is useful for when you want the filters to be
	 * visible but the column to be hidden from the table.
	 */
	hideColumn?: boolean;
	disableDrag?: boolean;
	// Include this column in data exports even when it’s hidden in the UI
	alwaysExport?: boolean;
}

export interface DataTableMeta {
	getFacetedUniqueValues?: (columnId: string) => Map<unknown, number> | undefined;
	getFacetedMinMaxValues?: (columnId: string) => [number, number] | undefined;
}

const baseFeatures = {
	columnFacetingFeature,
	columnFilteringFeature,
	globalFilteringFeature,
	columnOrderingFeature,
	columnSizingFeature,
	columnResizingFeature,
	columnVisibilityFeature,
	rowPaginationFeature,
	rowSelectionFeature,
	rowSortingFeature,
	columnFilterDefFeature,
	filterFns: {
		arrIncludes: filterFn_arrIncludes,
		equals: filterFn_equals,
		inDateRange: filterFn_inDateRange,
		includesString: filterFn_includesString,
		inNumberRange: filterFn_inNumberRange,
		weakEquals: filterFn_weakEquals,
	},
	sortFns: {
		alphanumeric: sortFn_alphanumeric,
		basic: sortFn_basic,
		datetime: sortFn_datetime,
		text: sortFn_text,
	},
	columnMeta: metaHelper<DataTableColumnMeta>(),
	tableMeta: metaHelper<DataTableMeta>(),
};

type DataTableRowModels = Pick<
	TableFeatures,
	| 'filteredRowModel'
	| 'sortedRowModel'
	| 'paginatedRowModel'
	| 'facetedRowModel'
	| 'facetedUniqueValues'
	| 'facetedMinMaxValues'
>;

export type DataTableFeatures = typeof baseFeatures & DataTableRowModels;

export type DataTableInstance<TRow extends RowData> = ReactTable<DataTableFeatures, TRow>;

export function createDataTableFeatures(rowModels: DataTableRowModels = {}): DataTableFeatures {
	return tableFeatures<DataTableFeatures>({
		...baseFeatures,
		...rowModels,
		facetedUniqueValues: (table, columnId) => {
			const getDerivedValues = rowModels.facetedUniqueValues?.(table, columnId);
			return () =>
				getMeta(table)?.getFacetedUniqueValues?.(columnId) ??
				getDerivedValues?.() ??
				emptyFacetedUniqueValues;
		},
		facetedMinMaxValues: (table, columnId) => {
			const getDerivedValues = rowModels.facetedMinMaxValues?.(table, columnId);
			return () => getMeta(table)?.getFacetedMinMaxValues?.(columnId) ?? getDerivedValues?.();
		},
	});
}

const emptyFacetedUniqueValues = new Map<unknown, number>();

function getMeta(table: Table<DataTableFeatures, RowData>) {
	return table.options.meta;
}
